import { type RefObject } from 'react';

/**
 * Calculate the scroll amount in pixels for tabs based on container and configuration
 * Similar to getHorizontalListScrollPx but adapted for tabs
 * @param gutter - The horizontal gutter between tabs
 * @param paddingLeft - Left padding of the tab container
 * @param paddingRight - Right padding of the tab container
 * @param componentMaxWidth - Maximum width of the component
 * @param ref - Reference to the tab container
 * @param tabItemSize - The tab item size configuration
 * @returns The scroll amount in pixels
 */
export function getTabScrollPx({
  gutter,
  paddingLeft,
  paddingRight,
  componentMaxWidth,
  ref,
  tabItemSize,
}: {
  gutter: number;
  paddingLeft: number;
  paddingRight: number;
  componentMaxWidth: number | undefined;
  ref: any;
  tabItemSize: string;
}) {
  if (!ref?.current) return 200; // Fallback

  const isSpacingEqual: boolean = gutter === paddingLeft;

  if (!componentMaxWidth) {
    const clientWidth = ref?.current?.clientWidth;
    if (isSpacingEqual) return clientWidth - paddingLeft;
    return clientWidth - paddingLeft - paddingRight;
  }

  if (isSpacingEqual) return componentMaxWidth - paddingLeft - paddingRight;

  if (!paddingRight) return componentMaxWidth - paddingLeft;

  return componentMaxWidth - paddingLeft - gutter;
}

/**
 * Check if tabs can scroll left
 * @param ref - Reference to the tab container
 * @returns True if can scroll left
 */
export function getCanScrollLeft({ ref }: { ref: any }): boolean {
  if (!ref?.current) return false;
  return ref.current.scrollLeft > 0;
}

/**
 * Check if tabs can scroll right
 * @param ref - Reference to the tab container
 * @returns True if can scroll right
 */
export function getCanScrollRight({ ref }: { ref: any }): boolean {
  if (!ref?.current) return false;
  const { scrollLeft, scrollWidth, clientWidth } = ref.current;
  return scrollLeft < scrollWidth - clientWidth;
}

/**
 * Scrolls a selected tab to the center of the visible area
 * @param ref - Reference to the tab container
 * @param tabIndex - Index of the tab to center
 */
export function scrollTabToCenter({
  ref,
  tabIndex,
}: {
  ref: RefObject<HTMLDivElement>;
  tabIndex: number;
}) {
  if (!ref?.current) return;

  const container = ref.current;
  const tabElements = container.querySelectorAll('[role="tab"]');
  const targetTab = tabElements[tabIndex] as HTMLElement;

  if (!targetTab) return;

  // Get container dimensions
  const containerRect = container.getBoundingClientRect();
  const containerWidth = containerRect.width;
  const containerCenter = containerWidth / 2;

  // Get target tab dimensions and position
  const tabRect = targetTab.getBoundingClientRect();
  const tabWidth = tabRect.width;
  const tabLeft = tabRect.left - containerRect.left;
  const tabCenter = tabLeft + tabWidth / 2;

  // Calculate the scroll offset needed to center the tab
  const currentScrollLeft = container.scrollLeft;
  const scrollOffset = tabCenter - containerCenter;

  // Calculate the new scroll position
  const newScrollLeft = currentScrollLeft + scrollOffset;

  // Ensure we don't scroll beyond the container bounds
  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  const finalScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));

  // Scroll to the calculated position
  container.scrollTo({
    left: finalScrollLeft,
    behavior: 'smooth',
  });
}
